import { Injectable, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { ConstantsService } from 'app/services/constants-service';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { EntityIdDetails } from 'app/shared/models/entity-id-details.model';
import { formatDisplayDate } from 'app/shared/utils/date-format.util';

/**
 * Service for entity monitoring tab.
 */
@Injectable()
export class EntityMonitoringService {
    entityDetailsUpdated :EventEmitter<EntityIdDetails> = new EventEmitter();
    /** Emitted when sanction/PEP/bad press match data may have changed (e.g. after resolving a hit). */
    matchDataChanged: EventEmitter<void> = new EventEmitter<void>();
    private entityIdDetails: EntityIdDetails;

    constructor(private requestService: ObservableRequestService) {
        this.entityIdDetails = new EntityIdDetails();
    }

    setEntityIdDetails(entityIdDetails: EntityIdDetails): void {
        this.entityIdDetails = entityIdDetails;
        this.entityDetailsUpdated.emit(this.entityIdDetails);
    }

    getBadPressHistory(entityId: number): Observable<any> {
        const badPressHistoryUri = ConstantsService.MONITORING_BADPRESS_HISTORY + entityId;
        return this.requestService.getRequest(badPressHistoryUri).map(response => {
            return response.arrResult || { suggestionList: [] };
        });
    };

    getSanctionHistory(entityId: number): Observable<any> {
        const sanctionUri = ConstantsService.MONITORING_SANCTIONS_HISTORY + entityId;
        return this.requestService.getRequest(sanctionUri).map(response => {
            return response.arrResult || { suggestionList: [] };
        });
    };

    getPepHistory(entityId: number): Observable<any> {
        const pepUri = ConstantsService.MONITORING_PEP_HISTORY + entityId;
        return this.requestService.getRequest(pepUri).map(response => {
            return response.arrResult || { suggestionList: [] };
        });
    };

    /**
     * One-time ad-hoc Beamar screening for the entity. Does not store results.
     * Returns list of SanctionEntity from Beamar screening service.
     */
    verifyNow(entityId: number): Observable<any> {
        const verifyUri = ConstantsService.MONITORING_VERIFY_NOW + entityId;
        return this.requestService.getRequest(verifyUri);
    }

    /**
     * One-time Beamar sanction search for an arbitrary name (not tied to an entity). Same match API as Verify now.
     */
    instantScreening(name: string, screeningType: 'PERSON' | 'ORGANIZATION'): Observable<any> {
        const encodedName = encodeURIComponent((name || '').trim());
        const type = screeningType === 'ORGANIZATION' ? 'ORGANIZATION' : 'PERSON';
        const uri =
            ConstantsService.MONITORING_INSTANT_SCREENING +
            '?name=' +
            encodedName +
            '&type=' +
            encodeURIComponent(type);
        return this.requestService.getRequest(uri);
    }

    /**
     * Fetch monitoring status for the entity (whether it is tracked in the screening service).
     */
    getMonitoringStatus(entityId: number): Observable<any> {
        const statusUri = ConstantsService.MONITORING_STATUS + entityId;
        return this.requestService.getRequest(statusUri);
    }

    /**
     * Register the entity for permanent/continuous monitoring with the screening service.
     * Sends entity data to Beamar screening service and stores the screening entity ID.
     */
    track(entityId: number): Observable<any> {
        const trackUri = ConstantsService.MONITORING_MONITOR + entityId;
        return this.requestService.postRequest(trackUri, {}, this.requestService.getJSONContentType());
    }

    /**
     * Disable tracking: unregister the entity from the screening service.
     */
    untrack(entityId: number): Observable<any> {
        const trackUri = ConstantsService.MONITORING_MONITOR + entityId;
        return this.requestService.deleteRequest(trackUri, {}, this.requestService.getJSONContentType());
    }

    /**
     * Delete all monitoring hits (sanctions, PEP, bad press) for an entity. Admin only.
     */
    deleteAllHits(entityId: number): Observable<any> {
        const uri = ConstantsService.MONITORING_DELETE_ALL_HITS + entityId;
        return this.requestService.deleteRequest(uri, {}, this.requestService.getJSONContentType());
    }

    /**
     * Delete all open (unresolved) monitoring hits for the current tenant. Admin only.
     * Resolved history is kept; entities remain registered for continuous screening.
     */
    deleteAllOpenHits(): Observable<any> {
        return this.requestService.deleteRequest(
            ConstantsService.MONITORING_DELETE_ALL_OPEN_HITS,
            {},
            this.requestService.getJSONContentType());
    }

    /**
     * Force a fresh Beamar screening (sanctions + PEP) for the given entity. Routes
     * through the trustgrc-side `/monitoring/rescreen/{id}` endpoint that proxies to
     * the new tenant rescreen API on Beamar.
     */
    rescreen(entityId: number): Observable<any> {
        const uri = ConstantsService.MONITORING_RESCREEN + entityId;
        return this.requestService.postRequest(uri, {}, this.requestService.getJSONContentType());
    }

    getEntityIdDetails(): EntityIdDetails {
        return this.entityIdDetails;
    }

    getDateForPrint(rawDate)
    {
        return formatDisplayDate(rawDate);
    }

}



// WEBPACK FOOTER //
// ./src/app/entities/monitoring/entity-monitoring.service.ts